import { EllipsisVerticalIcon, TrashIcon } from "lucide-react";
import React from "react";

import { Card, CardContent, CardHeader, PermissionGuard } from "@/components";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirmDialog } from "@/context/confirm-dialog";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";

import { TPlatform } from "../platform.types";

interface IPlatformsTableProps {
  platforms: TPlatform[];
  isLoading: boolean;
  onEditPlatform: (platform: TPlatform) => void;
  onDeletePlatform: (platformId: string) => void;
  isDeleting?: boolean;
  onBulkDeletePlatforms?: (platformIds: string[]) => void;
  isBulkDeleting?: boolean;
  showOwner?: boolean;
  showActions?: boolean;
  title?: string;
  emptyMessage?: string;
}

export const PlatformsTable: React.FC<IPlatformsTableProps> = ({
  platforms,
  isLoading,
  onEditPlatform,
  onDeletePlatform,
  isDeleting = false,
  onBulkDeletePlatforms,
  isBulkDeleting = false,
  showOwner = false,
  showActions = true,
  title = "Plataformas",
  emptyMessage = "Nenhuma plataforma encontrada para este cliente.",
}) => {
  const { openDialog } = useConfirmDialog();
  const { hasAnyPermission, hasPermission } = usePermissions();

  // Verificar se o usuário tem pelo menos uma permissão para ações
  const hasActionPermissions =
    showActions && hasAnyPermission(["platform:update", "platform:delete"]);

  const canBulkDelete = Boolean(onBulkDeletePlatforms) && hasPermission("platform:delete");

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  // Remove da seleção IDs que saíram da lista (ex: após exclusão bem-sucedida)
  React.useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => platforms.some((platform) => platform.id === id))
    );
  }, [platforms]);

  const allSelected =
    platforms.length > 0 && selectedIds.length === platforms.length;

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? platforms.map((platform) => platform.id) : []);
  };

  const toggleSelectOne = (platformId: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, platformId] : prev.filter((id) => id !== platformId)
    );
  };

  const handleDeletePlatform = (platform: TPlatform) => {
    openDialog(
      "Confirmar exclusão",
      `Tem certeza que deseja excluir a plataforma "${platform.landing_page?.title || 'Sem título'}"? Esta ação não pode ser desfeita.`,
      () => onDeletePlatform(platform.id)
    );
  };

  const handleBulkDeletePlatforms = () => {
    if (!onBulkDeletePlatforms || selectedIds.length === 0) return;
    const count = selectedIds.length;
    openDialog(
      "Confirmar exclusão em massa",
      `Tem certeza que deseja excluir ${count} ${count === 1 ? "plataforma" : "plataformas"}? Esta ação não pode ser desfeita.`,
      () => {
        onBulkDeletePlatforms(selectedIds);
        setSelectedIds([]);
      }
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">{title}</h3>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando plataformas...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (platforms.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">{title}</h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-lg font-semibold">
            {title} ({platforms.length})
          </h3>
          {canBulkDelete && selectedIds.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {selectedIds.length}{" "}
                {selectedIds.length === 1 ? "selecionada" : "selecionadas"}
              </span>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleBulkDeletePlatforms}
                disabled={isBulkDeleting}
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {isBulkDeleting ? "Excluindo..." : "Excluir selecionadas"}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {canBulkDelete && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(checked) => toggleSelectAll(Boolean(checked))}
                    aria-label="Selecionar todas as plataformas"
                  />
                </TableHead>
              )}
              <TableHead>Título</TableHead>
              {showOwner && <TableHead>Parceiro</TableHead>}
              <TableHead>Qtd de Planos</TableHead>
              <TableHead>Data de Criação</TableHead>
              <TableHead>Link</TableHead>
              {hasActionPermissions && (
                <TableHead>Ações</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {platforms.map((platform) => (
              <TableRow key={platform.id}>
                {canBulkDelete && (
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.includes(platform.id)}
                      onCheckedChange={(checked) =>
                        toggleSelectOne(platform.id, Boolean(checked))
                      }
                      aria-label={`Selecionar plataforma ${platform.landing_page?.title || ""}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  {platform.landing_page?.title}
                </TableCell>
                {showOwner && (
                  <TableCell>
                    {platform.organization_name || "—"}
                  </TableCell>
                )}
                <TableCell>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {platform.plans?.length || 0}{" "}
                    {(platform.plans?.length || 0) === 1 ? "plano" : "planos"}
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(platform.created_at).toLocaleDateString("pt-BR")}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="link"
                    className="text-blue-600 underline hover:text-blue-800 transition-colors p-0 h-auto"
                    onClick={() => {
                      if (platform.landing_page?.lp_link) {
                        window.open(
                          platform.landing_page.lp_link,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    }}
                  >
                    Abrir Landing Page
                  </Button>
                </TableCell>
                {hasActionPermissions && (
                  <TableCell>
                    <PermissionGuard
                      permissions={["platform:update", "platform:delete"]}
                      requireAll={false}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size={"sm"} variant={"ghost"} className="p-2">
                            <EllipsisVerticalIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="p-auto">
                          <PermissionGuard permission="platform:update">
                            <DropdownMenuItem
                              className="w-full"
                              onClick={() => onEditPlatform(platform)}
                            >
                              {t("generic.edit")}
                            </DropdownMenuItem>
                          </PermissionGuard>
                          <PermissionGuard permission="platform:delete">
                            <DropdownMenuItem
                              className="bg-red-400 w-full hover:bg-red-600"
                              onClick={() => handleDeletePlatform(platform)}
                              disabled={isDeleting}
                            >
                              <TrashIcon className="h-4 w-4 mr-2" />
                              {isDeleting ? "Excluindo..." : t("generic.delete")}
                            </DropdownMenuItem>
                          </PermissionGuard>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </PermissionGuard>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
